import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Users, Plus, Lock, Globe, Heart, Briefcase, Cross, 
  Sparkles, Eye, ArrowRight, MessageSquare, Crown, Search
} from 'lucide-react';

const API_URL = window.location.origin;

const CATEGORY_CONFIG = {
  career: { label: 'Career', icon: Briefcase, color: 'bg-blue-500' },
  relationships: { label: 'Relationships', icon: Heart, color: 'bg-pink-500' },
  faith: { label: 'Faith', icon: Cross, color: 'bg-purple-500' },
  rest: { label: 'Rest & Wellness', icon: Sparkles, color: 'bg-green-500' },
  visibility: { label: 'Visibility', icon: Eye, color: 'bg-amber-500' },
  other: { label: 'Other', icon: Users, color: 'bg-gray-500' }
};

const CirclesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [circleLimits, setCircleLimits] = useState({ max_members: 3, tier: 'Inner Circle' });
  
  const [newCircle, setNewCircle] = useState({
    name: '',
    description: '',
    category: 'other',
    is_private: false,
    max_members: 3
  });

  useEffect(() => {
    fetchCircles();
    fetchCircleLimits();
  }, [selectedCategory]);

  const fetchCircleLimits = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/circles/config/limits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCircleLimits(response.data);
      // Set default max_members to user's tier limit
      setNewCircle(prev => ({ ...prev, max_members: response.data.max_members }));
    } catch (error) {
      console.error('Failed to fetch circle limits:', error);
    }
  };

  const fetchCircles = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
      const response = await axios.get(`${API_URL}/api/circles${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCircles(response.data.circles || []);
    } catch (error) {
      console.error('Failed to fetch circles:', error);
      toast.error('Failed to load circles');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCircle = async () => {
    if (!newCircle.name.trim()) {
      toast.error('Please enter a circle name');
      return;
    }
    if (!newCircle.description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/circles`, newCircle, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Circle created! +15 diamonds earned');
      setCircles([response.data.circle, ...circles]);
      setShowCreateDialog(false);
      setNewCircle({
        name: '',
        description: '',
        category: 'other',
        is_private: false,
        max_members: circleLimits.max_members
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create circle');
    } finally {
      setCreating(false);
    }
  };

  const handleJoinCircle = async (circleId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/circles/${circleId}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Joined circle!');
      fetchCircles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join circle');
    }
  };

  const handleLeaveCircle = async (circleId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/circles/${circleId}/leave`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Left circle');
      fetchCircles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave circle');
    }
  };

  const filteredCircles = circles.filter(circle => {
    if (!searchQuery) return true;
    return circle.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
           circle.description.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Only show circles user is a member of (including retreat/special circles)
  const myCircles = filteredCircles.filter(c => c.is_member || c.id === 'legacy-circle');

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="circles-page">
        {/* Banner Image - Pouring Tea */}
        <div className="mb-8 rounded-2xl overflow-hidden shadow-lg relative h-48 md:h-56">
          <img 
            src="https://customer-assets.emergentagent.com/job_serene-edison/artifacts/1g94qc7d_Pouring%20tea.jpeg"
            alt="Sister Diamonds sharing tea"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent flex items-center">
            <div className="p-6 md:p-8">
              <p className="text-white text-sm md:text-base font-medium opacity-90">My Circles</p>
              <p className="text-white text-xl md:text-2xl font-heading font-bold">Pour Into Each Other</p>
            </div>
          </div>
        </div>
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-forest">Community Circles</h1>
            <p className="text-gray-600 mt-1">Connect with sisters who share your interests</p>
          </div>
          {/* Create Circle Button - HIDDEN: Member-created circles sunset to focus on curated community experiences
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-terracotta hover:bg-terracotta/90 text-white"
            data-testid="create-circle-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> Create Circle
          </Button>
          */}
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search circles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 input-base"
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* My Circles */}
        <div>
          <h2 className="font-heading text-xl font-semibold text-forest mb-4">My Circles</h2>
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading circles...</div>
          ) : myCircles.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myCircles.map(circle => (
                <CircleCard 
                  key={circle.id} 
                  circle={circle} 
                  onJoin={handleJoinCircle}
                  onLeave={handleLeaveCircle}
                  onClick={() => {
                    if (circle.id === 'legacy-circle') {
                      if ((user?.active_tiers || []).includes('legacy_circle') || user?.role === 'admin' || user?.role === 'super_admin') {
                        navigate('/legacy');
                      } else {
                        toast.info('The Legacy Circle is an invitation-only space');
                      }
                      return;
                    }
                    navigate(`/circles/${circle.id}`);
                  }}
                />
              ))}
            </div>
          ) : (
            <Card className="card-base">
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 mb-4">You haven't joined any circles yet.</p>
                <p className="text-sm text-gray-400">Circles you're a member of will appear here.</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Create Circle Dialog - HIDDEN: Feature sunset but code preserved
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-forest">Create a Circle</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="name">Circle Name</Label>
                <Input
                  id="name"
                  value={newCircle.name}
                  onChange={(e) => setNewCircle({ ...newCircle, name: e.target.value })}
                  placeholder="e.g., Career Changers Support"
                  className="input-base"
                  maxLength={50}
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newCircle.description}
                  onChange={(e) => setNewCircle({ ...newCircle, description: e.target.value })}
                  placeholder="What is this circle about? Who should join?"
                  className="input-base min-h-[80px]"
                  maxLength={300}
                />
              </div>

              <div>
                <Label>Category</Label>
                <Select 
                  value={newCircle.category} 
                  onValueChange={(val) => setNewCircle({ ...newCircle, category: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex items-center gap-2">
                          <config.icon className="h-4 w-4" />
                          {config.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Private Circle</Label>
                  <p className="text-sm text-gray-500">Only invited members can join</p>
                </div>
                <Switch
                  checked={newCircle.is_private}
                  onCheckedChange={(val) => setNewCircle({ ...newCircle, is_private: val })}
                />
              </div>

              <div>
                <Label>Max Members</Label>
                <p className="text-xs text-gray-500 mb-2">
                  Your {circleLimits.tier} membership allows up to {circleLimits.max_members} members per circle
                </p>
                <Select 
                  value={String(newCircle.max_members)} 
                  onValueChange={(val) => setNewCircle({ ...newCircle, max_members: parseInt(val) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: circleLimits.max_members }, (_, i) => i + 1).map(num => (
                      <SelectItem key={num} value={String(num)}>{num} {num === 1 ? 'member' : 'members'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="p-3 bg-terracotta/10 rounded-lg text-sm text-terracotta">
                <Sparkles className="h-4 w-4 inline mr-2" />
                You&apos;ll earn 15 diamonds for creating a circle!
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateCircle}
                disabled={creating}
                className="bg-terracotta hover:bg-terracotta/90 text-white"
              >
                {creating ? 'Creating...' : 'Create Circle'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        End of Create Circle Dialog - HIDDEN */}
      </div>
    </DashboardLayout>
  );
};

const CircleCard = ({ circle, onJoin, onLeave, onClick }) => {
  const config = CATEGORY_CONFIG[circle.category] || CATEGORY_CONFIG.other;
  const Icon = config.icon;

  return (
    <Card 
      className="card-base hover:shadow-md transition-shadow cursor-pointer group"
      onClick={onClick}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className={`p-2 rounded-lg ${config.color} text-white`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex items-center gap-2">
            {circle.is_archived && (
              <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 bg-amber-50" data-testid={`circle-archived-badge-${circle.id}`}>
                Archived
              </Badge>
            )}
            {circle.is_private && (
              <Badge variant="outline" className="text-xs">
                <Lock className="h-3 w-3 mr-1" /> Private
              </Badge>
            )}
            {circle.is_creator && (
              <Badge className="bg-terracotta text-white text-xs">
                <Crown className="h-3 w-3 mr-1" /> Creator
              </Badge>
            )}
          </div>
        </div>

        <h3 className="font-heading font-semibold text-forest mb-1 group-hover:text-terracotta transition-colors">
          {circle.name}
        </h3>
        <p className="text-sm text-gray-600 line-clamp-2 mb-3">{circle.description}</p>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <Users className="h-4 w-4" />
            <span>{circle.member_count} / {circle.max_members}</span>
          </div>

          {circle.is_member ? (
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={(e) => { e.stopPropagation(); onClick(); }}
                className="bg-forest hover:bg-forest/90 text-white"
                data-testid={`enter-circle-${circle.id}`}
              >
                Enter <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); onLeave(circle.id); }}
                className="text-gray-500 hover:text-red-500 hover:border-red-500"
              >
                Leave
              </Button>
            </div>
          ) : circle.id === 'legacy-circle' ? (
            <Badge variant="outline" className="text-xs text-gray-500">
              <Lock className="h-3 w-3 mr-1" /> Invitation Only
            </Badge>
          ) : (
            <Button
              size="sm"
              onClick={(e) => { e.stopPropagation(); onJoin(circle.id); }}
              className="bg-forest hover:bg-forest/90 text-white"
            >
              Join <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CirclesPage;
